import { MESSAGE_AUTHOR_TYPE } from '../../models/messages/Messages'

import type { LyraLibraryResource, Message } from '../../models/messages/Messages'

export enum CHAT_MESSAGE_SENDER_V2 {
  CLIENT = 1,
  ASSISTANT = 2,
}

export enum CHAT_MESSAGE_CONTENT_TYPE_V2 {
  TEXT = 1,
  MARKDOWN = 2,
}

export enum CHAT_MESSAGE_TYPE_V2 {
  GREETING = 1,
  CHAT = 2,
  SYSTEM = 3,
  HIGH_ALERT_TIMEOUT = 4,
}

export enum HIGH_ALERT_RISK_LEVEL_V2 {
  UNSPECIFIED = 0,
  YELLOW = 1,
  AMBER = 2,
  RED = 3,
}

export type ChatMessageV2 = {
  message_id: string
  conversation_id: string
  content: {
    type: CHAT_MESSAGE_CONTENT_TYPE_V2
    text: string
  }
  sender: CHAT_MESSAGE_SENDER_V2
  timestamp: string
  metadata?: Record<string, unknown> | null
  message_type: CHAT_MESSAGE_TYPE_V2
  lyra_library_resources?: LyraLibraryResource[]
}

export type ChatResponseV2 = {
  message: ChatMessageV2
  high_alert_risk_level?: HIGH_ALERT_RISK_LEVEL_V2
  high_risk_assessment?: Record<string, unknown> | null
  guardrail_type?: number
}

export type SubmittedMessageV2 = {
  body: string
  clientId: string
  metadata?: Record<string, unknown> | null
}

const HIGH_ALERT_RISK_LEVEL_NAMES: Partial<
  Record<HIGH_ALERT_RISK_LEVEL_V2, { highAlertRiskLevel: string; riskLevel: string }>
> = {
  [HIGH_ALERT_RISK_LEVEL_V2.YELLOW]: {
    highAlertRiskLevel: 'RISK_LEVEL_YELLOW',
    riskLevel: 'YELLOW',
  },
  [HIGH_ALERT_RISK_LEVEL_V2.AMBER]: {
    highAlertRiskLevel: 'RISK_LEVEL_AMBER',
    riskLevel: 'AMBER',
  },
  [HIGH_ALERT_RISK_LEVEL_V2.RED]: {
    highAlertRiskLevel: 'RISK_LEVEL_RED',
    riskLevel: 'RED',
  },
}

/**
 * Normalizes the V2 GenAI response and the submitted client message into the
 * legacy message shape consumed by member web. The V2 endpoint returns only
 * the assistant message, so the client message is added after the request
 * succeeds and both are immediately marked as sent.
 */
export const mapChatResponseV2ToMessages = (
  response: ChatResponseV2,
  submittedMessage: SubmittedMessageV2,
): Message[] => {
  const { message, high_alert_risk_level: highAlertRiskLevel } = response
  const dateCreated = message.timestamp || new Date().toISOString()
  const highAlertRiskLevelNames = highAlertRiskLevel ? HIGH_ALERT_RISK_LEVEL_NAMES[highAlertRiskLevel] : undefined
  const assistantMetadata = {
    ...(message.metadata ?? {}),
    gen_ai_message_id: message.message_id,
    ...(highAlertRiskLevelNames
      ? {
          high_alert_risk_level: highAlertRiskLevelNames.highAlertRiskLevel,
          risk_level: highAlertRiskLevelNames.riskLevel,
        }
      : {}),
    ...(message.lyra_library_resources?.length ? { attached_resources: message.lyra_library_resources } : {}),
  }

  const clientMessage: Message = {
    attributes: JSON.stringify({
      message_type: 'direct',
      from: submittedMessage.clientId,
      author_type: MESSAGE_AUTHOR_TYPE.PATIENT,
      metadata: submittedMessage.metadata ?? null,
    }),
    body: submittedMessage.body,
    conversationId: message.conversation_id,
    date_created: dateCreated,
    message_id: `${message.message_id}-client`,
    sendStatus: 'sent',
  }

  const assistantMessage: Message = {
    attributes: JSON.stringify({
      message_type: 'direct',
      author_type: MESSAGE_AUTHOR_TYPE.AI_COACH,
      metadata: assistantMetadata,
    }),
    body: message.content.text,
    conversationId: message.conversation_id,
    date_created: dateCreated,
    message_id: message.message_id,
    sendStatus: 'sent',
  }

  return [clientMessage, assistantMessage]
}
